/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Modal, FormControl, TextField, IconButton, MenuItem, Select } from "@mui/material";
import CustomButton from "../../components/buttons/CustomButton";
import CloseIcon from "@mui/icons-material/Close";
import MessageIcon from "../../../assets/images/messageIcon.svg";
import { SelectedAlertsContext } from "../../../utils/SelectedAlertContext";
import styles from "./popup.module.css";
import { buyerComment, otherRecommendationFeedback } from "Samarth Module/api/mdApi";
import { getRecommendationKey } from "utils/createRecommendationKey";
import { useMsal } from "@azure/msal-react";

const feedbackOptions = ["Material In-transit",
    "Material in GR/Unloading",
    "JIT Material",
    "Material available in blocked stock",
    "Expedite Past due PO",
    "Plan change/Schedule change",
    "PO Generation post contract issues closure",
    "Other"
];

const getAlertTypeKey = (alertType) => {
    if (alertType === "RMPM Alert") {
        return "rmpm";
    }
    if (alertType === "Contract Alert") {
        return "contract";
    }
    return "agileOr";
};

const getDisplayLabel = (alert) => alert?.Label || "Other Recommendation";

const getOtherRecommendationObject = (alert) => {
    const resolveData = alert?.resolveData || [];
    let otherRecommendationObj = resolveData.find(
        (item) => item.Title === "Other Recommendation"
    );

    if (resolveData.length === 1 && resolveData[0].title !== "Other Recommendation") {
        otherRecommendationObj = resolveData[0];
    }

    return otherRecommendationObj;
};

const getUpdatedRecommendation = (otherRecommendationObj, isCustomOtherRecommendation, selectedRecommendation) => ({
    ...otherRecommendationObj,
    Title: isCustomOtherRecommendation ? "Other Recommendation" : selectedRecommendation,
    Info: isCustomOtherRecommendation ? selectedRecommendation : "",
    uniqueKey: getRecommendationKey(otherRecommendationObj)
});

const getUpdatedResolveData = (alert, otherRecommendationObj, selectedRecommendation) =>
    alert?.resolveData?.map((recommend) =>
        recommend.Title === "Other Recommendation"
            ? {
                ...otherRecommendationObj,
                Title: selectedRecommendation,
                Info: "",
                uniqueKey: getRecommendationKey(otherRecommendationObj)
            }
            : recommend
    );

const upsertAcceptedAlert = (updatedSelections, alertTypeKey, alert, updatedAlert) => {
    const acceptedList = updatedSelections[alertTypeKey].accepted;
    const existingAlertIndex = acceptedList.findIndex(
        (item) => item["Alert ID"] === alert["Alert ID"]
    );

    if (existingAlertIndex !== -1) {
        acceptedList[existingAlertIndex] = updatedAlert;
        return;
    }

    acceptedList.push(updatedAlert);
};

const FeedbackPopup = ({ open, onClose, alertId, type, onConfirm }) => {
    const { accounts } = useMsal();
    const activeAccount = accounts[0] || null;
    const { setAlertDetailsData, setSelections } = useContext(SelectedAlertsContext);
    const [feedback, setFeedback] = useState("");
    const [feedbackOption, setFeedbackOption] = useState("");

    useEffect(() => {
        if (!open) return;
        setFeedback("");
        setFeedbackOption("");
    }, [open, alertId?.["Alert ID"]]);

    const isSameAlertRow = (item, selectedAlert) => {
        if (!item || !selectedAlert) return false;
        if (String(item["Alert ID"]) !== String(selectedAlert["Alert ID"])) return false;

        if (type === "RMPM Alert") {
            return (
                String(item["Material Id"]) === String(selectedAlert["Material Id"]) &&
                String(item["Site code"]) === String(selectedAlert["Site code"]) &&
                String(item["Week"]) === String(selectedAlert["Week"])
            );
        }

        if (type === "Agile OR") {
            return (
                String(item["CBU code"]) === String(selectedAlert["CBU code"]) &&
                String(item["Week"]) === String(selectedAlert["Week"])
            );
        }

        return true;
    };

    // Helper to get selected recommendation based on type
    const getSelectedRecommendation = () => {
        if (type !== "RMPM Alert") return feedback;
        return feedbackOption;
    };

    // Helper to build payload for feedback submission
    const buildFeedbackPayload = (alertId, selectedRecommendation) => {
        const payload = {
            alertId: alertId["Alert ID"],
            plant: alertId["Site code"],
            recommendation: selectedRecommendation,
            type: type,
        };

        if (type === "RMPM Alert") {
            payload.materialId = alertId["Material Id"];
            payload.week = parseInt(alertId["Week"].split("Week ")[1]);
        } else {
            payload.CBU_code = alertId["CBU code"];
            payload.week = alertId["Week"];
        }

        return payload;
    };

    // Helper to handle accepted RMPM alert buyer comment
    const handleAcceptedAlertComment = async (alertId, payload) => {
        await buyerComment({
            ...payload,
            Alert: alertId,
            comment: feedback,
            username: activeAccount.username,
        });
        setAlertDetailsData((prevData) =>
            prevData.map((item) =>
                isSameAlertRow(item, alertId) ? { ...item, BuyerComments: feedback } : item
            )
        );
    };

    // Helper to handle other recommendation feedback
    const handleOtherRecommendation = async (alertId, payload) => {
        await otherRecommendationFeedback(payload);
        setAlertDetailsData((prevData) =>
            prevData.map((item) => {
                if (!isSameAlertRow(item, alertId)) return item;
                const updatedResolveData = item.resolveData?.map((recommend) =>
                    recommend.Title === "Other Recommendation" || recommend.Title === alertId?.Label
                        ? { ...recommend, Title: payload.recommendation }
                        : recommend
                );
                return {
                    ...item,
                    Recommendation: payload.recommendation,
                    resolveData: updatedResolveData,
                    RecommendationNew: payload.recommendation,
                };
            })
        );
    };

    // handling the submit for the feeedback for other Recommendation
    const handleSubmitFeedback = async (alertId) => {
        if (type !== "RMPM Alert" && type !== "Agile OR") return;

        try {
            const selectedRecommendation = getSelectedRecommendation();
            const payload = buildFeedbackPayload(alertId, selectedRecommendation);

            const isAcceptedRmpm = type === "RMPM Alert" && alertId["Alert status"] === "Accepted";
            if (isAcceptedRmpm) {
                await handleAcceptedAlertComment(alertId, payload);
            } else {
                await handleOtherRecommendation(alertId, payload);
            }

            // Only add to cart if alert is not already accepted
            if (type === "RMPM Alert" && alertId["Alert status"] !== "Accepted") {
                setAlert(alertId, selectedRecommendation);
            }
            if (onConfirm) onConfirm(selectedRecommendation);
        } catch (error) {
            console.error("Error executing actions:", error);
        } finally {
            setFeedback("");
            setFeedbackOption("");
            onClose();
        }
    };

    // for closing the feedback popup
    const CancelFeedbackPopup = () => {
        setFeedback('');
        setFeedbackOption('');
        onClose();
    };

    // handling input change
    const handleCustomRecommendation = (event) => {
        setFeedback(event.target.value);
    };

    const setAlert = (alert, selectedRecommendation) => {
        setSelections((prev) => {
            // Create a deep copy of the previous selections
            const updatedSelections = JSON.parse(JSON.stringify(prev));

            // Determine the alert type key
            const alertTypeKey = getAlertTypeKey(type);

            // Ensure the alert type category exists
            if (!updatedSelections[alertTypeKey]) {
                updatedSelections[alertTypeKey] = { accepted: [], rejected: [] };
            }

            // Find the "Other Recommendation" object from resolveData (if exists)
            const otherRecommendationObj = getOtherRecommendationObject(alert);

            const isCustomOtherRecommendation =
                type === 'RMPM Alert' && feedbackOption === 'Other';

            const updatedRecommendation = getUpdatedRecommendation(
                otherRecommendationObj,
                isCustomOtherRecommendation,
                selectedRecommendation
            );

            const updatedResolveData = getUpdatedResolveData(
                alert,
                otherRecommendationObj,
                selectedRecommendation
            );

            // Create the updated alert object
            const updatedAlert = {
                ...alert,
                Recommendation: selectedRecommendation,
                RecommendationNew: selectedRecommendation,
                resolveData: updatedResolveData,
                selectedRecom: [updatedRecommendation]
            };

            upsertAcceptedAlert(updatedSelections, alertTypeKey, alert, updatedAlert);

            return updatedSelections;
        });
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className={styles.modalContainer}>
                <div className={styles.dialogBox}>
                    {/* header & close btn */}
                    <div className={styles.dialogBoxContent}>
                        <img src={MessageIcon} alt="Message icon" />
                        <div className={styles.rejectContainer}>
                            <Typography className={styles.header}>{getDisplayLabel(alertId)}</Typography>
                            <IconButton onClick={onClose}>
                                <CloseIcon className={styles.closeIcon} />
                            </IconButton>
                        </div>
                    </div>
                    {type === "RMPM Alert" ?
                        (<div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <Typography className={styles.alertTitle}>
                                Please select the option for the recommendation:
                            </Typography>
                            <Select
                                value={feedbackOption}
                                onChange={(e) => {
                                    const selectedOption = e.target.value;
                                    setFeedbackOption(selectedOption);
                                }}
                                size="small"
                                className={styles.rejectedDropdown}
                            >
                                {feedbackOptions.map((option) => (
                                    <MenuItem className={styles.listItem} key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                        ) :
                        (<FormControl style={{ width: '100%' }}>
                            <TextField
                                size="small"
                                autoFocus={true}
                                autoComplete="off"
                                value={feedback}
                                onChange={handleCustomRecommendation}
                                placeholder="Please specify"
                                inputProps={{
                                    style: { fontSize: "12px", width: '100%' },
                                }}
                            />
                        </FormControl>
                        )
                    }
                    { }
                    <div className={`${styles.dialogBtn} ${styles.marginTopBtns}`}>
                        <CustomButton label="Cancel" type="popupCancel" onClick={CancelFeedbackPopup} />
                        <CustomButton label="Submit" disabled={!(feedback || feedbackOption)} type="reject" onClick={() => handleSubmitFeedback(alertId)} />
                    </div>
                </div>
            </div>
        </Modal>
    )
}

FeedbackPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    alertId: PropTypes.shape({
        "Alert ID": PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        "Site code": PropTypes.string,
        "Material Id": PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        "Week": PropTypes.string,
        "CBU code": PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        "Label": PropTypes.string,
        "Alert status": PropTypes.string,
        resolveData: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    type: PropTypes.string,
    onConfirm: PropTypes.func,
};

export default FeedbackPopup;